import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import { TDatabaseId, tg } from '@infotech-soe/metaschema';
import { Set as ImmutableSet } from 'immutable';

import { arrayExt } from '../../../framework/language/ArrayExt';
import { DatasetParamValues } from '../../../infrastructure/providers/abstract/IApiProvider';
import { DisableNodeStrategy } from '../../../models/category/categoryTree';
import { CatalogsStateEntity } from '../catalogs';
import { HierarchyStateEntity } from '../hierarchy';

import {
  CategoryTreeNode,
  IExtendedTreeNodeInstance,
} from './CategoryTreeNode';
import { SiblingNodesList } from './SiblingNodesList';

export class CategoryTree {
  constructor(
    private readonly state: CatalogsStateEntity | HierarchyStateEntity
  ) {}

  public setRoots(
    nodes: IExtendedTreeNodeInstance[],
    params?: DatasetParamValues
  ): void {
    const nodeList = new SiblingNodesList(nodes);
    this.state.treeRootId = nodes.length === 1 ? nodes[0].id : undefined;
    this.state.nodes = nodeList.toArray();
    if (params) {
      (this.state as HierarchyStateEntity).params = params;
    }
  }

  public setChildren(
    parentId: TDatabaseId,
    children: ITreeNodeInstance[]
  ): void {
    if (this.findNode(parentId).children.length > 0) {
      this.replaceChildren(parentId, children);
    } else {
      this.addNewChildren(parentId, children);
    }
  }

  private replaceChildren(
    parentId: TDatabaseId,
    children: ITreeNodeInstance[]
  ): void {
    const prevActive = this.state.activeId;
    const wasExpanded = this.state.expandedNodes.has(parentId);

    this.collapseNode(parentId);

    this.state.childrenCache = this.state.childrenCache.set(parentId, children);

    if (wasExpanded) {
      this.state.expandedNodes = this.state.expandedNodes.add(parentId);
      this.restoreChildrenFromCache(parentId);
    }

    if (tg.isSomething(prevActive) && this.exists(prevActive)) {
      try {
        this.activateNode(prevActive);
      } catch {
        this.activateNode(parentId);
      }
    } else if (this.state.activeId === null) {
      this.activateNode(parentId);
    }
  }

  private addNewChildren(
    parentId: TDatabaseId,
    children: ITreeNodeInstance[]
  ): void {
    this.state.childrenCache = this.state.childrenCache.set(parentId, children);
    this.restoreChildrenFromCache(parentId);
  }

  private restoreChildrenFromCache(parentId: TDatabaseId): void {
    const children = this.state.childrenCache.get(parentId);
    if (children) {
      const nodes = new SiblingNodesList(children).toArray();
      this.appendNodes(parentId, nodes);
    }
  }

  private appendNodes(parentId: TDatabaseId, nodes: CategoryTreeNode[]): void {
    const parentNodeIndex = this.findIndex(parentId);
    this.addNodesAtIndex(parentNodeIndex + 1, nodes);
    this.connectNodeWithChildren(parentNodeIndex, nodes);
    this.restoreInnerNodes(nodes);
  }

  private addNodesAtIndex(index: number, nodes: CategoryTreeNode[]): void {
    this.state.nodes.splice(index, 0, ...nodes);
  }

  private connectNodeWithChildren(
    parentNodeIndex: number,
    childNodes: CategoryTreeNode[]
  ): void {
    const parentNode = this.state.nodes[parentNodeIndex];

    for (const node of childNodes) {
      parentNode.addChildNode(node);
    }
  }

  private restoreInnerNodes(nodes: CategoryTreeNode[]): void {
    for (const node of nodes) {
      if (this.state.expandedNodes.has(node.id)) {
        this.restoreChildrenFromCache(node.id);
      }
    }
  }

  public expandNode(nodeId: TDatabaseId): void {
    const alreadyExpanded = this.state.expandedNodes.has(nodeId);
    if (!alreadyExpanded && this.findNode(nodeId).hasChildren) {
      const node = this.findNode(nodeId);
      this.state.expandedNodes = this.state.expandedNodes.add(nodeId);
      // Відновлювати дітей тільки якщо їх ще немає в дереві
      if (node.children.length === 0) {
        this.restoreChildrenFromCache(nodeId);
      }
    }
  }

  private findNode(nodeId: TDatabaseId): CategoryTreeNode {
    return this.state.nodes[this.findIndex(nodeId)];
  }

  private findIndex(nodeId: TDatabaseId): number {
    const index = this.state.nodes.findIndex(node => node.id === nodeId);

    if (index === -1) {
      this.state.expandedNodes = ImmutableSet(
        this.state.expandedNodes.filter(id => id === 0)
      );
      return 0;
    }

    return index;
  }

  public expandActiveNode(): void {
    this.expandNode(this.getActiveNode().id);
  }

  public getActiveNode(): CategoryTreeNode {
    return this.state.nodes[this.getActiveIndex()];
  }

  private getActiveIndex(): number {
    if (!this.hasActive()) {
      throw new Error('No active catalog');
    }

    return this.findIndex(this.state.activeId ?? 0);
  }

  private hasActive(): boolean {
    return this.state.activeId !== null;
  }

  public collapseNode(nodeId: TDatabaseId): void {
    if (this.state.expandedNodes.has(nodeId)) {
      this.state.expandedNodes = this.state.expandedNodes.delete(nodeId);
      this.removeChildren(nodeId);
    } else {
      this.activateParentIfNotRoot(nodeId);
    }
  }

  private removeChildren(parentId: TDatabaseId): void {
    const parentNodeIndex = this.findIndex(parentId);
    const parentNode = this.state.nodes[parentNodeIndex];
    this.purgeChildrenTree(parentNode);
    this.removeImmediateChildren(parentNodeIndex);
  }

  private activateParentIfNotRoot(nodeId: TDatabaseId): void {
    if (!this.findNode(nodeId).root) {
      this.activateParent(nodeId);
    }
  }

  private activateParent(nodeId: TDatabaseId): void {
    const parentNode = this.findParentNode(nodeId);
    this.activateNode(parentNode.id);
  }

  private findParentNode(nodeId: TDatabaseId): CategoryTreeNode {
    const node = this.findNode(nodeId);
    if (node.parentId === null) {
      throw new Error('No parent node');
    }
    return this.findNode(node.parentId);
  }

  private purgeChildrenTree(node: CategoryTreeNode): void {
    for (const childId of node.children) {
      this.removeChildren(childId);
      this.selectParentIfActive(childId);
    }
  }

  private selectParentIfActive(nodeId: TDatabaseId): void {
    if (this.hasActive() && this.getActiveNode().id === nodeId) {
      this.activateParent(nodeId);
    }
  }

  private removeImmediateChildren(nodeIndex: number): void {
    const node = this.state.nodes[nodeIndex];
    this.saveChildrenToCache(node);
    this.removeNodesAtIndex(nodeIndex + 1, node.children.length);
    node.children = [];
  }

  private saveChildrenToCache(node: CategoryTreeNode): void {
    const nodes = node.children.map(id => this.findNode(id).toServerModel());
    if (nodes.length > 0) {
      this.state.childrenCache = this.state.childrenCache.set(node.id, nodes);
    } else {
      this.state.childrenCache = this.state.childrenCache.delete(node.id);
    }
  }

  private removeNodesAtIndex(index: number, count: number): void {
    this.state.nodes.splice(index, count);
  }

  public collapseActiveNode(): void {
    this.collapseNode(this.getActiveNode().id);
  }

  public markPending(nodeId: TDatabaseId): void {
    this.state.pendingNodes = this.state.pendingNodes.add(nodeId);
  }

  public unmarkPending(nodeId: TDatabaseId): void {
    this.state.pendingNodes = this.state.pendingNodes.delete(nodeId);
  }

  public setNodeChecked(nodeId: TDatabaseId): void {
    this.state.checkedNodes = this.state.checkedNodes.add(nodeId);
  }

  public unsetNodeChecked(nodeId: TDatabaseId): void {
    this.state.checkedNodes = this.state.checkedNodes.delete(nodeId);
  }

  public toggleNodeChecks(parentId: TDatabaseId, nodeIds: TDatabaseId[]): void {
    this.state.checkedNodes.has(parentId)
      ? nodeIds.forEach(nodeId => this.unsetNodeChecked(nodeId))
      : nodeIds.forEach(nodeId => this.setNodeChecked(nodeId));
  }

  public activateNext(): void {
    if (this.hasActive()) {
      this.updateSelectionWithOffset(1);
    }
  }

  public activatePrevious(): void {
    if (this.hasActive()) {
      this.updateSelectionWithOffset(-1);
    }
  }

  private updateSelectionWithOffset(offset: number): void {
    const currentIndex = this.getActiveIndex();
    const newIndex = currentIndex + offset;

    if (newIndex >= 0 && newIndex < this.state.nodes.length) {
      this.activateNodeByIndex(newIndex);
    }
  }

  private activateNodeByIndex(index: number): void {
    const node = this.state.nodes[index];
    this.activateNode(node.id);
  }

  public activateNode(nodeId: TDatabaseId): void {
    this.state.activeId = nodeId;

    if (this.hasHierarchyRoot()) {
      this.cancelHierarchyOnLeave();
    }
  }

  private hasHierarchyRoot(): boolean {
    return this.state.hierarchyRootId !== null;
  }

  private cancelHierarchyOnLeave(): void {
    const active = this.getActiveNode();
    const root = this.state.hierarchyRootId;

    if (tg.isSomething(root) && !active.nodePath.has(root)) {
      this.unsetHierarchyRoot();
    }
  }

  public unsetHierarchyRoot(): void {
    this.state.hierarchyRootId = null;
  }

  public setHierarchyRoot(nodeId: TDatabaseId): void {
    this.clearChecksAndHierarchy();
    this.state.hierarchyRootId = nodeId;
  }

  public setActiveAsHierarchyRoot(): void {
    this.setHierarchyRoot(this.getActiveNode().id);
  }

  public clearChecksAndHierarchy(): void {
    this.state.checkedNodes = this.state.checkedNodes.clear();
    this.state.hierarchyRootId = null;
  }

  public addNode(parentId: TDatabaseId, node: CategoryTreeNode): void {
    this.findNode(parentId).addHiddenChildNode(node);
    this.collapseNode(parentId);
    this.appendToChildrenCache(parentId, node.toServerModel());
    this.expandNode(parentId);
    this.activateNode(node.id);
  }

  private appendToChildrenCache(
    parentId: TDatabaseId,
    node: ITreeNodeInstance
  ): void {
    const children = this.state.childrenCache.get(parentId);
    if (children) {
      children.push(node);
    } else {
      this.state.childrenCache = this.state.childrenCache.set(parentId, [node]);
    }
  }

  public deleteNode(nodeId: TDatabaseId): void {
    const parent = this.findParentNode(nodeId);
    const siblingsCount = parent.children.length;
    this.unsetNodeChecked(nodeId);
    this.collapseNode(parent.id);

    if (siblingsCount === 1) {
      parent.hasChildren = false;
    }

    this.removeFromChildrenCache(parent.id, nodeId);
    this.expandNode(parent.id);
  }

  private removeFromChildrenCache(
    parentId: TDatabaseId,
    nodeId: TDatabaseId
  ): void {
    const children = this.state.childrenCache.get(parentId);
    if (children) {
      arrayExt(children).findAndRemove(node => node.id === nodeId);
    }
  }

  public renameNode(nodeId: TDatabaseId, name: string): void {
    const index = this.findIndex(nodeId);
    const node = this.state.nodes[index];
    this.state.nodes[index] = Object.assign(
      Object.create(Object.getPrototypeOf(node)),
      node,
      { name, label: name }
    );
  }

  public moveNode(nodeId: TDatabaseId, newParentId: TDatabaseId): void {
    const node = this.findNode(nodeId);
    this.deleteNode(nodeId);
    this.addNode(newParentId, node);
    this.activateNode(nodeId);
  }

  public exists(nodeId: TDatabaseId): boolean {
    try {
      return !!this.findIndex(nodeId);
    } catch {
      return false;
    }
  }

  public disableNode(
    nodeId: TDatabaseId,
    strategy: DisableNodeStrategy = DisableNodeStrategy.SINGLE
  ): void {
    const node = this.findNode(nodeId);
    node.hasAccess = false;
    node.disableStrategy = strategy;

    if (strategy === DisableNodeStrategy.RECURSIVE) {
      node.children.forEach(child => this.disableNode(child, strategy));
    }
  }
}
